/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React from 'react';

import { TimeFrameEnumType } from 'models/TimeframeModels';
import theme from 'theme';
import { getMomentAgo, getMomentAgoFromTimeFrameEnumType } from 'utils/dateTimeHelpers';
import { isMobile } from 'utils/responsive';

import { Typography } from 'components/_common/Typography';

const GraphSkeleton: React.FC = () => {
  const isMobileDevice = isMobile(useBreakpointIndex());
  return (
    <div sx={{ height: '115px' }}>
      <svg
        width="194"
        height="113"
        viewBox="0 0 194 113"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width="192"
          height="106"
          transform="translate(1)"
          fill={isMobileDevice ? theme.colors.deepSea10 : theme.colors.white}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M66.25 34.7163C66.25 33.6117 67.1454 32.7163 68.25 32.7163H76.5184C77.6229 32.7163 78.5184 33.6117 78.5184 34.7163V106.327H66.25V34.7163ZM131.457 65.4312C130.352 65.4312 129.457 66.3266 129.457 67.4312V106.326H143.77V67.4312C143.77 66.3266 142.875 65.4312 141.77 65.4312H131.457ZM3 65.432C1.89543 65.432 1 66.3274 1 67.432V106.327H15.3131V67.432C15.3131 66.3274 14.4177 65.432 13.3131 65.432H3ZM35.625 49.0737C34.5204 49.0737 33.625 49.9692 33.625 51.0737V106.326H47.9381V51.0737C47.9381 49.9692 47.0427 49.0737 45.9381 49.0737H35.625ZM162.082 51.0737C162.082 49.9692 162.977 49.0737 164.082 49.0737H174.395C175.5 49.0737 176.395 49.9692 176.395 51.0737V106.326H162.082V51.0737ZM98.832 49.0737C97.7275 49.0737 96.832 49.9692 96.832 51.0737V106.326H111.145V51.0737C111.145 49.9692 110.25 49.0737 109.145 49.0737H98.832ZM17.3125 59.2534C17.3125 58.1488 18.2079 57.2534 19.3125 57.2534H29.6256C30.7302 57.2534 31.6256 58.1488 31.6256 59.2534V106.327H17.3125V59.2534ZM147.77 57.2534C146.665 57.2534 145.77 58.1488 145.77 59.2534V106.327H160.083V59.2534C160.083 58.1488 159.187 57.2534 158.083 57.2534H147.77ZM178.395 42.896C178.395 41.7914 179.29 40.896 180.395 40.896H190.708C191.812 40.896 192.708 41.7914 192.708 42.896V106.327H178.395V42.896ZM82.5195 40.896C81.415 40.896 80.5195 41.7914 80.5195 42.896V106.327H94.8326V42.896C94.8326 41.7914 93.9372 40.896 92.8326 40.896H82.5195ZM49.9375 42.896C49.9375 41.7914 50.8329 40.896 51.9375 40.896H62.2506C63.3552 40.896 64.2506 41.7914 64.2506 42.896V106.327H49.9375V42.896ZM115.145 57.2534C114.04 57.2534 113.145 58.1488 113.145 59.2534V106.327H127.458V59.2534C127.458 58.1488 126.562 57.2534 125.458 57.2534H115.145Z"
          fill="#EDF0F2"
        />
        <mask id="path-2-inside-1" fill="white">
          <path d="M0 106H4.04167V109.999C4.04167 111.104 3.14624 111.999 2.04167 111.999H2C0.895432 111.999 0 111.104 0 109.999V106Z" />
        </mask>
        <path
          d="M0 106H4.04167V109.999C4.04167 111.104 3.14624 111.999 2.04167 111.999H2C0.895432 111.999 0 111.104 0 109.999V106Z"
          fill="#C9CACE"
          stroke="#C9CACE"
          strokeWidth="4.04167"
          mask="url(#path-2-inside-1)"
        />
        <mask id="path-3-inside-2" fill="white">
          <path d="M189.957 107H193.999V110.999C193.999 112.104 193.103 112.999 191.999 112.999H191.957C190.852 112.999 189.957 112.104 189.957 110.999V107Z" />
        </mask>
        <path
          d="M189.957 107H193.999V110.999C193.999 112.104 193.103 112.999 191.999 112.999H191.957C190.852 112.999 189.957 112.104 189.957 110.999V107Z"
          fill="#C9CACE"
          stroke="#C9CACE"
          strokeWidth="4.04167"
          mask="url(#path-3-inside-2)"
        />
        <line x1="0.5" y1="106.5" x2="193.5" y2="106.5" stroke="#C9CACE" strokeLinecap="round" />
      </svg>
    </div>
  );
};

type CaseIncidenceSummarySkeletonProps = {
  timeFrame: TimeFrameEnumType;
};
const CaseIncidenceSummarySkeleton: React.FC<CaseIncidenceSummarySkeletonProps> = ({
  timeFrame,
}) => {
  const dateFormat = 'MMM YYYY';
  return (
    <div data-testid="card-incidence-summary-skeleton">
      <div sx={{ display: ['block', 'flex'], marginTop: ['12px', '16px'] }}>
        <div
          sx={{
            width: ['100%', '194px'],
            display: ['flex', null],
            flexDirection: ['column', null],
            alignItems: ['center', null],
            backgroundColor: [theme.colors.deepSea10, theme.colors.white],
            marginBottom: ['4px', '0'],
          }}
        >
          <GraphSkeleton />
          <div
            sx={{
              width: '194px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="caption" color="stone50">
              {getMomentAgoFromTimeFrameEnumType(timeFrame).format(dateFormat)}
            </Typography>
            <Typography variant="caption" color="stone50">
              {getMomentAgo('start-of-today').format(dateFormat)}
            </Typography>
          </div>
        </div>
        <div
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            marginLeft: [null, '24px'],
          }}
        >
          {[1, 2, 3, 4].map(it => (
            <div
              key={it}
              data-testid="location-case-summary-skeleton"
              sx={{
                width: 'calc(50% - 2px)',
                height: '62px',
                padding: '15px 12px',
                backgroundColor: 'deepSea10',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                marginLeft: it % 2 != 0 ? '0' : '4px',
                marginBottom: it < 3 ? '4px' : '0',
              }}
            >
              <div sx={{ width: '60px', height: '12px', backgroundColor: 'deepSea20' }} />
              <div sx={{ width: '118px', height: '12px', backgroundColor: 'deepSea20' }} />
            </div>
          ))}
        </div>
      </div>
      <div sx={{ width: '240px', height: '12px', bg: 'deepSea20', mt: '24px' }} />
      <div sx={{ width: '240px', height: '20px', bg: 'deepSea20', mt: ['24px', '32px'] }} />
    </div>
  );
};

export default CaseIncidenceSummarySkeleton;
